import { db } from '@/utils/firebase';
import { logError, logInfo } from '@/utils/logger';
import { User } from 'firebase/auth';
import {
  Timestamp,
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  runTransaction,
  setDoc,
  where,
} from 'firebase/firestore';
interface ReferralData {
  referralCode: string;
  userId: string;
  referredUsers: string[];
  hasUsedReferralCode: boolean;
  referredBy?: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}
class FirestoreService {
  private static REFERRALS_COLLECTION = 'referrals';
  private static FEEDBACK_COLLECTION = 'feedback';
  private static async generateUniqueReferralCode(): Promise<string> {
    const generateCode = () =>
      Math.random().toString(36).substring(2, 8).toUpperCase();
    let code = generateCode();
    let isUnique = false;
    while (!isUnique) {
      const referralRef = doc(db, this.REFERRALS_COLLECTION, code);
      const referralDoc = await getDoc(referralRef);
      if (!referralDoc.exists()) {
        isUnique = true;
      } else {
        code = generateCode();
      }
    }
    return code;
  }
  static async createOrUpdateUserReferral(user: User): Promise<string> {
    try {
      const existingReferral = await this.getUserReferralData(user.uid);
      if (existingReferral) {
        return existingReferral.referralCode;
      }
      const referralCode = await this.generateUniqueReferralCode();
      const referralRef = doc(db, this.REFERRALS_COLLECTION, referralCode);
      const referralData: ReferralData = {
        referralCode,
        userId: user.uid,
        referredUsers: [],
        hasUsedReferralCode: false,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };
      await setDoc(referralRef, referralData);
      logInfo('User referral data created', 'createOrUpdateUserReferral', {
        userId: user.uid,
        referralCode,
      });
      return referralCode;
    } catch (error) {
      logError(error, 'createOrUpdateUserReferral');
      throw error;
    }
  }
  static async applyReferralCode(
    user: User,
    referralCode: string,
  ): Promise<boolean> {
    try {
      const referralRef = doc(db, this.REFERRALS_COLLECTION, referralCode);
      return await runTransaction(db, async (transaction) => {
        const referralDoc = await transaction.get(referralRef);
        if (!referralDoc.exists()) {
          logInfo('Invalid referral code', 'applyReferralCode', {
            referralCode,
          });
          return false;
        }
        const referralData = referralDoc.data() as ReferralData;
        if (referralData.userId === user.uid) {
          logInfo(
            'User cannot use their own referral code',
            'applyReferralCode',
            { userId: user.uid },
          );
          return false;
        }
        const userReferralQuery = query(
          collection(db, this.REFERRALS_COLLECTION),
          where('userId', '==', user.uid),
        );
        const userReferralSnapshot = await getDocs(userReferralQuery);
        if (!userReferralSnapshot.empty) {
          const userReferralDoc = userReferralSnapshot.docs[0];
          const userReferralData = userReferralDoc.data() as ReferralData;
          if (userReferralData.hasUsedReferralCode) {
            logInfo(
              'User has already used a referral code',
              'applyReferralCode',
              { userId: user.uid },
            );
            return false;
          }
          transaction.update(userReferralDoc.ref, {
            hasUsedReferralCode: true,
            referredBy: referralCode,
            updatedAt: Timestamp.now(),
          });
        } else {
          const newUserReferralCode = await this.generateUniqueReferralCode();
          const newUserReferralRef = doc(
            db,
            this.REFERRALS_COLLECTION,
            newUserReferralCode,
          );
          transaction.set(newUserReferralRef, {
            referralCode: newUserReferralCode,
            userId: user.uid,
            referredUsers: [],
            hasUsedReferralCode: true,
            referredBy: referralCode,
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
          });
        }
        transaction.update(referralRef, {
          referredUsers: arrayUnion(user.uid),
          updatedAt: Timestamp.now(),
        });
        logInfo('Referral code applied successfully', 'applyReferralCode', {
          userId: user.uid,
          referralCode,
        });
        return true;
      });
    } catch (error) {
      logError(error, 'applyReferralCode');
      throw error;
    }
  }
  static async getUserReferralData(
    userId: string,
  ): Promise<ReferralData | null> {
    try {
      const referralsRef = collection(db, this.REFERRALS_COLLECTION);
      const q = query(referralsRef, where('userId', '==', userId));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const referralDoc = querySnapshot.docs[0];
        return referralDoc.data() as ReferralData;
      }
      return null;
    } catch (error) {
      logError(error, 'getUserReferralData');
      throw error;
    }
  }
  static async hasUserUsedReferralCode(userId: string): Promise<boolean> {
    try {
      const referralData = await this.getUserReferralData(userId);
      return referralData ? referralData.hasUsedReferralCode : false;
    } catch (error) {
      logError(error, 'hasUserUsedReferralCode');
      throw error;
    }
  }
  static async getOrGenerateReferralCode(user: User): Promise<string> {
    try {
      return await this.createOrUpdateUserReferral(user);
    } catch (error) {
      logError(error, 'getOrGenerateReferralCode');
      throw error;
    }
  }
  static async submitFeedback(user: User, feedback: string): Promise<boolean> {
    try {
      const feedbackRef = doc(collection(db, this.FEEDBACK_COLLECTION));
      await setDoc(feedbackRef, {
        userId: user.uid,
        feedback,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
      logInfo('Feedback submitted successfully', 'submitFeedback', {
        userId: user.uid,
        feedbackId: feedbackRef.id,
      });
      return true;
    } catch (error) {
      logError(error, 'submitFeedback');
      throw new Error('Failed to submit feedback. Please try again.');
    }
  }
}
export default FirestoreService;
